import { useEffect, useRef, useState } from "react";
import {
  useCreateRejectionReasonMutation,
  useDeleteRejectionReasonMutation,
  useGetDeclineReasonsQuery,
  useUpdateRejectionReasonMutation,
} from "../../../graphql/generated";
import useGqlClient from "../../../hooks/useGqlClient";
import { useTheme } from "../../../hooks/useTheme";
import styled, { css } from "../../../styles";
import { Button, ButtonWrap, CTA } from "../../CTA";
import { CardDivider } from "../../Divider";
import { Drawer } from "../../Drawer";
import { Dropdown } from "../../Dropdown";
import { Flex } from "../../Flex";
import Loader from "../../Loader";
import { Underline } from "../../Tabs";
import { Text } from "../../Text";
import { View } from "../../View";
import { AddButton } from "../../icons/AddIcon";
import { EditIcon } from "../../icons/EditIcon";
import { TemplateIcon } from "../../icons/TemplateIcon";
import { TrashIcon } from "../../icons/TrashIcon";
import { SendMessageIcon } from "../../icons/chat/SendMessageIcon";

const StyledFlex = styled(Flex)`
  display: flex;
  align-items: stretch;
  flex-direction: column;
`;

interface Props {
  onConfirm: (info: string, internalRejectionReason: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  firstName: string;
  brandName: string;
}

export const RejectOverlay = (props: Props) => {
  const [rejectionMessage, setRejectionMessage] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const mainTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isMainComposerFocused, setIsMainComposerFocused] = useState(false);
  const theme = useTheme();

  const adjustMainComposerHeight = () => {
    const ta = mainTextareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  };

  // Ensure autosize also occurs when a template is inserted programmatically
  useEffect(() => {
    adjustMainComposerHeight();
    setIsMainComposerFocused(true);
  }, [rejectionMessage]);

  const applyTemplate = (tpl: string) => {
    const firstName = props.firstName?.replace(/^@/, "").split(" ")[0] || "";
    // Support {{firstName}}, {{ first_name }}, {{ firstname }}
    return tpl
      .replace(/\{\{\s*first[_ ]?name\s*\}\}/gi, firstName)
      .replace(/\{\{\s*brand[_ ]?name\s*\}\}/gi, props.brandName);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  return (
    <>
      <Templates
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        onSelect={(reason) => setRejectionMessage(applyTemplate(reason))}
      />
      {props.isLoading ? (
        <Flex align="center" justify="center">
          <Loader />
        </Flex>
      ) : (
        <StyledFlex>
          <Text
            weight="bold"
            size="xl"
            margin={"0 0 0 0"}
            colorPreset="heading"
          >
            Decline Application
          </Text>
          <Underline selected />
          <Text margin={"m 0 s 0"} colorPreset="secondary">
            We’ll let them know so they can look at other opportunities
          </Text>

          <Text weight="bold" margin="s 0 xs" size="s">
            Message for Creator{" "}
            <span
              style={{
                fontWeight: 400,
                color: theme.color.typography.secondary,
              }}
            >
              (optional)
            </span>
          </Text>

          <Composer isFocused={isMainComposerFocused}>
            <ComposerTextarea
              ref={mainTextareaRef}
              placeholder="Add your message..."
              value={rejectionMessage}
              onChange={(e) => {
                const nextValue = e.currentTarget.value;
                setRejectionMessage(nextValue);
                adjustMainComposerHeight();
              }}
              onFocus={() => setIsMainComposerFocused(true)}
              onBlur={() => setIsMainComposerFocused(false)}
              rows={4}
            />
            {rejectionMessage.length === 0 ? (
              <AbsoluteWrap align="center">
                <FloatingWrap
                  align="center"
                  onClick={() => setShowTemplates(true)}
                  style={{
                    cursor: "pointer",
                  }}
                >
                  <TemplateIcon height={18} colorPreset="link" />
                  <Text size="s" margin="0 0 0 xs">
                    Use (or create) a quick template
                  </Text>
                  <Text
                    size="s"
                    weight="semi"
                    margin="0 0 0 s"
                    style={{
                      textDecoration: "underline",
                    }}
                  >
                    Try it
                  </Text>
                </FloatingWrap>
              </AbsoluteWrap>
            ) : null}
          </Composer>
          <ButtonWrap margin="xl 0 0">
            <CTA type="secondary" to="#" onClick={props.onCancel}>
              Cancel
            </CTA>
            <Button
              disabled={props.isLoading}
              onClick={() => props.onConfirm(rejectionMessage, "")}
            >
              {props.isLoading ? "Declining..." : "Decline"}
            </Button>
          </ButtonWrap>
        </StyledFlex>
      )}
    </>
  );
};

function Templates(props: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (reason: string) => void;
}) {
  const client = useGqlClient();
  const { data, isLoading, refetch } = useGetDeclineReasonsQuery(client);
  const { mutate: createRejectionReason } =
    useCreateRejectionReasonMutation(client);
  const { mutate: deleteRejectionReason } =
    useDeleteRejectionReasonMutation(client);
  const { mutate: updateRejectionReason } =
    useUpdateRejectionReasonMutation(client);

  type LocalReason = { id?: string; reason: string };
  const [reasons, setReasons] = useState<LocalReason[]>([]);
  const [newReason, setNewReason] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [editing, setEditing] = useState<{
    id?: string;
    value: string;
  } | null>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isEditFocused, setIsEditFocused] = useState(false);

  function insertPlaceholderAtCursor(placeholder: string) {
    const ta = textareaRef.current;
    if (!ta) return;
    const selectionStart = ta.selectionStart ?? 0;
    const selectionEnd = ta.selectionEnd ?? selectionStart;
    const before = newReason.slice(0, selectionStart);
    const after = newReason.slice(selectionEnd);
    const next = `${before}${placeholder}${after}`;
    setNewReason(next);
    // restore focus and place cursor after inserted text
    requestAnimationFrame(() => {
      ta.focus();
      const caret = selectionStart + placeholder.length;
      try {
        ta.setSelectionRange(caret, caret);
      } catch {}
    });
  }

  const adjustComposerHeight = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  };

  const adjustEditComposerHeight = () => {
    const ta = editTextareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${ta.scrollHeight}px`;
  };

  useEffect(() => {
    if (data?.rejectionReasons) {
      setReasons(
        data.rejectionReasons.map((r) => ({ id: r.id, reason: r.reason }))
      );
    }
  }, [data?.rejectionReasons]);

  // Autosize composer textarea on mount and when content changes
  useEffect(() => {
    adjustComposerHeight();
  }, [newReason]);

  // Autosize edit textarea when content changes
  useEffect(() => {
    if (!editing) return;
    adjustEditComposerHeight();
  }, [editing?.value, editing]);

  function handleSelect(reason: string) {
    props.onSelect(reason);
    props.onClose();
  }

  return (
    <Drawer
      width={520}
      isOpen={props.isOpen}
      onClose={() => {
        setCreateMode(false);
        setEditing(null);
        props.onClose();
      }}
    >
      <View margin="l">
        <Flex direction="column">
          <Flex direction="row" align="center" justify="space-between">
            <Text weight="bold" size="l" margin="0">
              Templates
            </Text>
          </Flex>
          <Underline selected />

          {isLoading ? (
            <Flex align="center" justify="center" margin="l 0">
              <Loader />
            </Flex>
          ) : null}

          {!isLoading ? (
            <Flex direction="column" margin="s 0 0">
              {reasons.length === 0 ? (
                <Text colorPreset="secondary" margin="s 0 0">
                  No templates yet.
                </Text>
              ) : null}
              {reasons.map((r, idx) => {
                const isEditing = editing?.id === r.id;
                return (
                  <Row
                    key={r.id ?? `row-${idx}`}
                    margin="0 0 0 0"
                    onClick={() => {
                      if (isEditing) return;
                      handleSelect(r.reason);
                    }}
                  >
                    {!isEditing ? (
                      <TemplateWrap>
                        <Text margin="0" style={{ whiteSpace: "pre-wrap" }}>
                          {r.reason || "(empty)"}
                        </Text>
                      </TemplateWrap>
                    ) : (
                      <>
                        <Composer isFocused={isEditFocused}>
                          <ComposerTextarea
                            ref={editTextareaRef}
                            value={editing?.value ?? ""}
                            onChange={(e) => {
                              const nextValue = e.currentTarget.value;
                              setEditing((prev) =>
                                prev ? { ...prev, value: nextValue } : prev
                              );
                              adjustEditComposerHeight();
                            }}
                            onFocus={() => setIsEditFocused(true)}
                            onBlur={() => setIsEditFocused(false)}
                            rows={3}
                          />
                        </Composer>
                      </>
                    )}
                    <ActionsWrap
                      className={!isEditing ? "actions" : undefined}
                      forceVisible={isEditing}
                    >
                      {!isEditing ? (
                        <>
                          <ActionIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!r.id) return;
                              setEditing({ id: r.id, value: r.reason });
                              requestAnimationFrame(() => {
                                editTextareaRef.current?.focus();
                              });
                            }}
                            title="Edit template"
                          >
                            <EditIcon
                              height={18}
                              width={18}
                              colorPreset="secondary"
                            />
                          </ActionIcon>
                          <ActionIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              const toDelete = reasons[idx];
                              if (!toDelete.id) return;
                              deleteRejectionReason(
                                { id: toDelete.id },
                                {
                                  onSuccess: () => {
                                    refetch();
                                  },
                                }
                              );
                            }}
                            title="Delete template"
                          >
                            <TrashIcon
                              height={16}
                              width={16}
                              colorPreset="secondary"
                            />
                          </ActionIcon>
                        </>
                      ) : (
                        <Button
                          size="xs"
                          buttonType="quaternary"
                          isDisabled={
                            !editing || !(editing.value || "").trim().length
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            const currentEditing = editing;
                            if (!currentEditing?.id) return;
                            const trimmed = (
                              currentEditing.value ?? ""
                            ).trimEnd();
                            if (!trimmed) return;
                            console.log("saveTemplateEdit", {
                              id: currentEditing.id,
                              reason: trimmed,
                            });
                            updateRejectionReason(
                              { id: currentEditing.id, reason: trimmed },
                              {
                                onSuccess: () => {
                                  refetch();
                                  setEditing(null);
                                },
                              }
                            );
                          }}
                        >
                          Save
                        </Button>
                      )}
                    </ActionsWrap>
                  </Row>
                );
              })}

              {createMode ? (
                <View>
                  <CardDivider margin="l 0 l" />
                  <Composer isFocused={isComposerFocused}>
                    <PersonalizeWrap>
                      <Dropdown
                        dropdownType="text"
                        size="s"
                        options={[
                          {
                            label: "Their first name",
                            value: "{{ first_name }}",
                          },
                          {
                            label: "Your brand name",
                            value: "{{ brand_name }}",
                          },
                        ]}
                        width={180}
                        anchor="bottom end"
                        renderLabel={() => "Insert field"}
                        selectionMode="single"
                        selectedOptions={[]}
                        setSelectedOptions={(selected) => {
                          if (selected.length === 0) return;
                          insertPlaceholderAtCursor(selected[0]);
                        }}
                      />
                    </PersonalizeWrap>
                    <ComposerTextarea
                      ref={textareaRef}
                      placeholder="Write a new template..."
                      value={newReason}
                      autoFocus
                      onChange={(e) => {
                        const nextValue = e.currentTarget.value;
                        setNewReason(nextValue);
                        adjustComposerHeight();
                      }}
                      onFocus={() => setIsComposerFocused(true)}
                      onBlur={() => setIsComposerFocused(false)}
                      rows={5}
                    />
                    <MessageSendButton
                      onClick={() => {
                        const trimmed = newReason.trimEnd();
                        if (!trimmed) return;
                        createRejectionReason(
                          { reason: trimmed },
                          {
                            onSuccess: () => {
                              refetch();
                              setNewReason("");
                              setCreateMode(false);
                            },
                          }
                        );
                      }}
                      title="Add template"
                    >
                      <SendMessageIcon
                        style={{ opacity: newReason.length === 0 ? 0.5 : 1 }}
                        width={28}
                        height={28}
                        colorPreset="link"
                      />
                    </MessageSendButton>
                  </Composer>
                </View>
              ) : (
                <View margin="l 0 0">
                  <AddButton
                    onAdd={() => setCreateMode(true)}
                    label="New template"
                  />
                </View>
              )}
            </Flex>
          ) : null}
        </Flex>
      </View>
    </Drawer>
  );
}

const TemplateWrap = styled.div`
  border: 1px solid ${(p) => p.theme.color.button.secondaryBorder};
  border-radius: ${(p) => p.theme.misc.borderRadiusSmall};
  background-color: ${(p) => p.theme.color.card.callout};
  cursor: pointer;
  flex: 1;
  padding: ${(p) => p.theme.spacing.m};
  &:hover {
    border-color: ${(p) => p.theme.color.typography.text};
  }
`;

const Row = styled(Flex)`
  justify-content: space-between;
  display: flex;
  align-items: center;
  position: relative;
  margin-top: ${(p) => p.theme.spacing.m};
  flex: 1;

  &:hover {
    .actions {
      opacity: 1;
    }
  }
`;

const ActionsWrap = styled.div<{ forceVisible?: boolean }>`
  position: absolute;
  right: ${(p) => p.theme.spacing.l};
  top: ${(p) => p.theme.spacing.m};
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.s};
  transition: opacity 0.15s ease-in-out;
  opacity: ${(p) => (p.forceVisible ? 1 : 0)};
`;

const ActionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    svg {
      filter: brightness(0.25);
    }
  }
`;

const Composer = styled.div<{ isFocused: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.spacing.m};
  border: 1px solid ${(p) => p.theme.color.typography.secondary}50;
  border-radius: ${(p) => p.theme.misc.borderRadius};
  padding: ${(p) => p.theme.spacing.m};
  position: relative;
  flex: 1;

  ${(p) =>
    p.isFocused &&
    css`
      border-color: ${p.theme.color.primary};
      box-shadow: 0px 0px 0px 2px ${p.theme.color.input.hover};
      outline: none;
    `}
`;

const ComposerTextarea = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  min-height: 24px;
  max-height: 420px;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  font-size: ${(p) => p.theme.typography.size.m};
  font-weight: ${(p) => p.theme.typography.weight.normal};
  color: ${(p) => p.theme.color.typography.text};
  line-height: 1.4;
  padding: 0;
  background: transparent;

  /* Custom scrollbar styling - align with chat input */
  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(p) => p.theme.color.typography.secondary}30;
    border-radius: 10px;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(p) => p.theme.color.typography.secondary}50;
    opacity: 1;
  }

  &:hover::-webkit-scrollbar-thumb,
  &:active::-webkit-scrollbar-thumb {
    opacity: 1;
  }

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &:hover,
  &:active {
    scrollbar-color: ${(p) => p.theme.color.typography.secondary}30 transparent;
  }
`;

const MessageSendButton = styled.div`
  margin-top: auto;
  border: none;
  outline: none;
  cursor: pointer;
  display: flex;
  align-items: flex-end;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.9);
  }
`;

const AbsoluteWrap = styled(Flex)`
  position: absolute;
  bottom: ${(p) => p.theme.spacing.m};
  left: 0;
  right: 0;
`;

const FloatingWrap = styled(Flex)`
  box-shadow: ${(p) => p.theme.shadow.cardMedium};
  border-radius: 999px;
  margin: 0 auto;
  color: ${(p) => p.theme.color.typography.text};
  background: ${(p) => p.theme.color.card.background};
  padding: ${(p) => p.theme.spacing.xs} ${(p) => p.theme.spacing.m};

  &:hover {
    box-shadow: ${(p) => p.theme.shadow.cardHeavy};
  }
`;

const PersonalizeWrap = styled(Flex)`
  position: absolute;
  top: 0;
  right: 0;
`;
